import type {
  GeneratedFormDefinition,
  GeneratedFormField,
  GeneratedFormValues,
} from '@template-ui/helpers/generated-form.helpers';
import type { WorkflowClientCommandReference } from './workflow-command-contracts';

export interface WorkflowFieldPolicyContext {
  workflowInstanceId: number;
  occurrenceId: number;
  configurationVersionId: number;
  definitionVersionId: number;
  definitionHash: string;
  command: WorkflowClientCommandReference;
  entityCode: string;
  policyCode: string;
  policyHash: string;
  classificationPolicyCode: string | null;
  evaluatedUtc: string;
}
export interface WorkflowRuntimeField {
  name: string;
  required: boolean;
  visible: boolean;
  editable: boolean;
  classification: string;
  masked: boolean;
  canUnmask: boolean;
}
export interface WorkflowRuntimeFieldPolicy {
  version: 1;
  context: WorkflowFieldPolicyContext;
  fields: WorkflowRuntimeField[];
}
export type WorkflowFieldPolicySource = Pick<
  WorkflowFieldPolicyContext,
  | 'workflowInstanceId'
  | 'occurrenceId'
  | 'configurationVersionId'
  | 'definitionVersionId'
  | 'definitionHash'
>;

const object = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const positive = (value: unknown) =>
  typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
const text = (value: unknown) =>
  typeof value === 'string' && value.length > 0 && value.length <= 200;
export function sameFieldPolicyCommand(
  left: WorkflowClientCommandReference,
  right: WorkflowClientCommandReference,
): boolean {
  return (
    !!left?.key &&
    !!right?.key &&
    left.key.provider === right.key.provider &&
    left.key.kind === right.key.kind &&
    left.key.code === right.key.code &&
    left.key.version === right.key.version &&
    left.inputSchemaHash === right.inputSchemaHash &&
    left.outputSchemaHash === right.outputSchemaHash &&
    left.contractHash === right.contractHash
  );
}

/** Evaluated server metadata narrows an installed form. Saved form settings remain a separate contract. */
export function applyRuntimeFieldPolicy(
  definition: GeneratedFormDefinition,
  policy: unknown,
  command: WorkflowClientCommandReference,
  source?: WorkflowFieldPolicySource,
  disabledOutcomes: readonly string[] = [],
): GeneratedFormDefinition | null {
  if (policy == null) return definition;
  if (
    !object(policy) ||
    policy['version'] !== 1 ||
    !Array.isArray(policy['fields']) ||
    !object(policy['context']) ||
    Object.keys(policy).some(
      (key) => !['version', 'context', 'fields'].includes(key),
    )
  )
    return null;
  const context = policy['context'];
  if (
    Object.keys(context).some(
      (key) =>
        ![
          'workflowInstanceId',
          'occurrenceId',
          'configurationVersionId',
          'definitionVersionId',
          'definitionHash',
          'command',
          'entityCode',
          'policyCode',
          'policyHash',
          'classificationPolicyCode',
          'evaluatedUtc',
        ].includes(key),
    )
  )
    return null;
  if (
    ![
      'workflowInstanceId',
      'occurrenceId',
      'configurationVersionId',
      'definitionVersionId',
    ].every((key) => positive(context[key])) ||
    !text(context['definitionHash']) ||
    context['entityCode'] !== definition.contract ||
    context['policyCode'] !== 'workflow.command.fields.v1' ||
    !text(context['policyHash']) ||
    typeof context['evaluatedUtc'] !== 'string' ||
    !Number.isFinite(Date.parse(context['evaluatedUtc'])) ||
    (context['classificationPolicyCode'] !== null &&
      !text(context['classificationPolicyCode'])) ||
    !object(context['command']) ||
    !sameFieldPolicyCommand(
      context['command'] as unknown as WorkflowClientCommandReference,
      command,
    )
  )
    return null;
  if (
    source &&
    Object.entries(source).some(([key, value]) => context[key] !== value)
  )
    return null;
  const fields = policy['fields'];
  if (
    fields.length !== definition.fields.length ||
    fields.length > 100 ||
    new Set(fields.map((field) => (object(field) ? field['name'] : null)))
      .size !== fields.length
  )
    return null;
  const resolved: GeneratedFormField[] = [];
  for (const base of definition.fields) {
    const field: unknown = fields.find(
      (field) => object(field) && field['name'] === base.name,
    );
    if (
      !object(field) ||
      Object.keys(field).some(
        (key) =>
          ![
            'name',
            'required',
            'visible',
            'editable',
            'classification',
            'masked',
            'canUnmask',
          ].includes(key),
      ) ||
      !['required', 'visible', 'editable', 'masked', 'canUnmask'].every(
        (key) => typeof field[key] === 'boolean',
      ) ||
      !['public', 'internal', 'restricted'].includes(
        String(field['classification']),
      )
    )
      return null;
    const required = base.required || field['required'] === true;
    const conditionalRequired =
      base.requiredWhen &&
      !(
        base.requiredWhen.field === 'outcome' &&
        disabledOutcomes.includes(base.requiredWhen.equals)
      ) &&
      definition.fields
        .find((item) => item.name === base.requiredWhen!.field)
        ?.options?.some((option) => option.value === base.requiredWhen!.equals);
    if (
      (!field['visible'] || !field['editable']) &&
      (required || conditionalRequired)
    )
      return null;
    if (!field['visible']) continue;
    resolved.push({
      ...base,
      required,
      readOnly: base.readOnly || !field['editable'],
      errorCodes: [
        ...new Set([...base.errorCodes, `workflow.form.field.${base.name}`]),
      ],
    });
  }
  return { ...definition, fields: resolved };
}

export function fieldPolicyKey(
  policy?: WorkflowRuntimeFieldPolicy | null,
): string | null {
  if (!policy) return null;
  if (!object(policy.context)) return JSON.stringify(policy);
  return JSON.stringify([
    Object.fromEntries(
      Object.entries(policy.context).filter(([key]) => key !== 'evaluatedUtc'),
    ),
    policy.fields,
  ]);
}

/** Drafts contain newly typed editable input only; protected initial data never becomes a draft. */
export function policyEditableValues(
  form: GeneratedFormDefinition,
  values: GeneratedFormValues,
): GeneratedFormValues {
  return Object.fromEntries(
    Object.entries(values).filter(([name]) =>
      form.fields.some((field) => field.name === name && !field.readOnly),
    ),
  );
}
export function policyInitialValues(
  form: GeneratedFormDefinition,
  policy: WorkflowRuntimeFieldPolicy | null | undefined,
  initial: GeneratedFormValues,
): GeneratedFormValues {
  return Object.fromEntries(
    Object.entries(initial).filter(
      ([name]) =>
        form.fields.some((field) => field.name === name) &&
        !policy?.fields.find((field) => field.name === name)?.masked,
    ),
  );
}

export function policyAllowsOutcomes(
  policy: WorkflowRuntimeFieldPolicy | null | undefined,
  outcomes: readonly { reasonPolicy: string; enabled?: boolean }[],
): boolean {
  const notes = policy?.fields.find((field) => field.name === 'notes');
  return (
    !notes ||
    (notes.visible && notes.editable) ||
    !outcomes.some(
      (outcome) =>
        outcome.enabled !== false && outcome.reasonPolicy === 'required',
    )
  );
}
